import { createWizard } from '@heidi/flow/wizard';

import type { PersonalizationStep } from '../../contracts';

/**
 * The onboarding personalization step flow — one declared list replaces the
 * four legacy flow arrays (`DESKTOP_FLOW`, `MOBILE_FLOW`,
 * `LEARN_BASICS_DESKTOP_FLOW`, `LEARN_BASICS_COMPACT_FLOW`): variant
 * membership lives on each step's `enabled` guard (flowKind × viewport ×
 * feature flags, implemented in `usePersonalizationForm`). The v2 and
 * learn-basics entry steps never coexist, so the declared order interleaves
 * both variants; each variant's filtered order matches its legacy array.
 *
 * `reconcile: 'backward'`: when a mid-flow viewport change disables the
 * current step (a learn-basics user on `mic-test` shrinking below `lg`), the
 * engine retreats to the nearest earlier surviving step so entered data is
 * preserved — never forward toward completion.
 *
 * The entry step varies by variant (`name` for v2, `about-you` for
 * learn-basics) and is seeded via `input.step` at mount. A user resuming after
 * the /auth/onboard page already answered `about-you` through `clinic`, so
 * those steps' guards are false for them: the flow starts on the first step
 * after `clinic` and neither Back nor `goTo` can reach the /auth/onboard steps.
 */
export const personalizationWizard = createWizard<PersonalizationStep>({
  id: 'onboardingPersonalization',
  reconcile: 'backward',
  steps: [
    { id: 'about-you', enabled: 'showsAboutYou', canGoBack: false },
    { id: 'name', enabled: 'showsNameAndRole', canGoBack: false },
    { id: 'role', enabled: 'showsNameAndRole' },
    { id: 'clinic', enabled: 'showsClinic' },
    // Guided-tour arm collects invites here instead of second-to-last. Same
    // component and same deferred send as `invite-team`; the two ids are
    // mutually exclusive via `isGuidedTour`.
    { id: 'tour-invite-team', enabled: 'showsGuidedTourInviteTeam', canGoBack: false },
    { id: 'mic-permissions', enabled: 'showsMicPermissions' },
    { id: 'template-creation', enabled: 'isV2DesktopFlow' },
    { id: 'template-loading', enabled: 'isV2DesktopFlow', canGoBack: false },
    { id: 'template-review', enabled: 'isV2DesktopFlow', canGoBack: false },
    { id: 'document-selection', enabled: 'showsDocumentSelection', canGoBack: false },
    { id: 'mic-test', enabled: 'isLearnBasicsPreviewFlow' },
    { id: 'note-ready', enabled: 'isControlNotePreviewFlow' },
    { id: 'note-edit', enabled: 'isControlNotePreviewFlow' },
    // Guided-tour arm: `note-edit`'s three accordion tasks, one screen each.
    { id: 'tour-detail', enabled: 'isGuidedTourFlow' },
    { id: 'tour-artefacts', enabled: 'isGuidedTourFlow' },
    { id: 'tour-evidence', enabled: 'isGuidedTourFlow' },
    // Collect emails only — send deferred in completeOnboarding after the
    // organisation_info PATCH provisions the Practice team.
    { id: 'invite-team', enabled: 'showsInviteTeam', canGoBack: false },
    { id: 'completion' },
  ],
});

/**
 * Steps that exist in the flow but are hidden from the progress bar (the old
 * `STEP_META.showInProgress: false`). Progress derives from
 * `wizardState.steps` minus these — see `usePersonalizationForm`.
 */
export const PROGRESS_HIDDEN_STEPS: ReadonlySet<PersonalizationStep> = new Set([
  'template-loading',
  'note-ready',
]);
